// Etapas do quadro (coluna da pauta): fonte única para o front, o server e a
// API do MCP. Cada cliente pode ter o próprio quadro em clients.columns_config
// (ids, rótulos, ordem); sem config, vale o ETAPAS_PADRAO. Out/26 (Agnes,
// emiteaí): o calendário e o MCP ainda mostravam "Roteiro" com a lista fixa
// antiga, e card em etapa fora do quadro do cliente sumia da tela.

export type EtapaDef = { id: string; label: string; color?: string };

// "sugestao_tema" é etapa de TEMA (temas.status = sugestao), não de pauta.
export const ETAPA_SUGESTAO = "sugestao_tema";

// Quadro de quem não tem config própria.
export const ETAPAS_PADRAO: EtapaDef[] = [
  { id: "sugestao_tema",  label: "Sugestão de Tema", color: "#A78BFA" },
  { id: "ideia",          label: "Roteiros",         color: "#0A0A0A" },
  { id: "roteiro",        label: "Roteiro",          color: "#7C3AED" },
  { id: "producao",       label: "Em Produção",      color: "#2563EB" },
  { id: "revisao",        label: "Aprov. Redação",   color: "#EC4899" },
  { id: "revisao_design", label: "Aprov. Design",    color: "#7C3AED" },
  { id: "ajuste",         label: "Em Ajuste",        color: "#52525B" },
  { id: "aprovado",       label: "Aprovado",         color: "#059669" },
  { id: "agendado",       label: "Agendado",         color: "#0891B2" },
  { id: "publicado",      label: "Publicado",        color: "#10B981" },
  { id: "desaprovado",    label: "Desaprovado",      color: "#EF4444" },
];

// Todos os valores que o banco aceita em pautas.coluna (ENUM).
export const COLUNAS_PAUTA_BANCO = [
  "ideia", "captacao", "roteiro", "producao", "producao_criacao", "revisao",
  "revisao_design", "ajuste", "aprovado", "agendado", "publicado", "desaprovado",
] as const;
export type ColunaPautaBanco = typeof COLUNAS_PAUTA_BANCO[number];

// Nome de cada etapa quando ela NÃO está no quadro do cliente (card antigo
// numa etapa que o cliente não usa). Só serve para avisar, nunca para listar.
const NOME_NEUTRO: Record<string, string> = {
  sugestao_tema: "Sugestão de Tema", ideia: "Ideação", captacao: "Em Captação",
  roteiro: "Roteiro", producao: "Em Produção", producao_criacao: "Em Produção de Criação",
  revisao: "Aprovação de Redação", revisao_design: "Aprovação de Design", ajuste: "Em Ajuste",
  aprovado: "Aprovado", agendado: "Agendado", publicado: "Publicado", desaprovado: "Desaprovado",
};

// Cor por etapa, igual para todo cliente (o rótulo é do cliente, a cor não).
export const ETAPA_COR: Record<string, string> = {
  sugestao_tema: "#9333EA", ideia: "#64748B", captacao: "#A16207", roteiro: "#0D9488",
  producao: "#0891B2", producao_criacao: "#0369A1", revisao: "#DB2777", revisao_design: "#C026D3",
  ajuste: "#EA580C", aprovado: "#2563EB", agendado: "#6366F1", publicado: "#15803D",
  desaprovado: "#DC2626",
};

// Etapas de fim de fluxo (pronto, no ar ou recusado).
export const ETAPAS_FIM = new Set(["aprovado", "agendado", "publicado", "desaprovado"]);

// Etapas "em andamento": tudo entre a etapa inicial e o fim, na ordem do quadro.
export function etapasEmAndamento(quadro: EtapaDef[]): EtapaDef[] {
  const ini = etapaInicial(quadro);
  return etapasDePauta(quadro).filter(c => c.id !== ini && !ETAPAS_FIM.has(c.id));
}

export function parseQuadro(raw: unknown): EtapaDef[] | null {
  if (!raw) return null;
  try {
    const p = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!Array.isArray(p)) return null;
    const ok = p.filter((c: any) => c && typeof c.id === "string" && typeof c.label === "string");
    return ok.length ? ok : null;
  } catch { return null; }
}

// Quadro efetivo do cliente: a config dele ou o padrão.
export function quadroDoCliente(cfg: EtapaDef[] | null | undefined): EtapaDef[] {
  return cfg && cfg.length ? cfg : ETAPAS_PADRAO;
}

// Só as etapas onde uma pauta pode estar (sem a de sugestão de tema).
export function etapasDePauta(quadro: EtapaDef[]): EtapaDef[] {
  return quadro.filter(c => c.id !== ETAPA_SUGESTAO);
}

export function estaNoQuadro(id: string | null | undefined, quadro: EtapaDef[]): boolean {
  return !!id && quadro.some(c => c.id === id);
}

export function rotuloEtapa(id: string | null | undefined, quadro: EtapaDef[]): string {
  if (!id) return "";
  return quadro.find(c => c.id === id)?.label ?? NOME_NEUTRO[id] ?? id;
}

// Etapa em que nasce a pauta de um tema aprovado ou de uma cópia sem etapa.
export function etapaInicial(quadro: EtapaDef[]): string {
  const pautas = etapasDePauta(quadro);
  return pautas.some(c => c.id === "ideia") ? "ideia" : (pautas[0]?.id ?? "ideia");
}
